import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError, timeout } from 'rxjs';

import { PaymentsDashboardRepository } from '@app/domain/ports/payments-dashboard.repository';
import { PaymentsDashboardError } from '@app/domain/models/payments-dashboard/payments-dashboard.error';
import type {
  PaymentsDashboardData,
  PaymentsDashboardQuery,
} from '@app/domain/models/payments-dashboard/payments-dashboard.model';
import type { PaymentsDashboardDto } from '@app/data/dtos/payments/payments-dashboard.dto';
import { mapPaymentsDashboard } from '@app/data/mappers/payments/payments-dashboard.mapper';
import { environment } from 'src/environments/environment';

/**
 * Clasifica el fallo SOLO por su status: el cuerpo (`detail`) nunca se lee, así
 * que no hay forma de que llegue a la pantalla. Un 404 cae en "no disponible"
 * (el endpoint solo existe en stage; en dev la vista lo dice sin romperse).
 */
export function toPaymentsDashboardError(error: unknown): PaymentsDashboardError {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 403) return new PaymentsDashboardError('forbidden');
    if (error.status === 400) return new PaymentsDashboardError('badRequest');
  }
  return new PaymentsDashboardError('unavailable');
}

/**
 * Dashboard de pagos sobre HTTP (`GET ${apiUrl}/api/v1/payments/dashboard/`).
 * Nunca manda `id_company`: el back recorta por los gestores del JWT, así que
 * desde esta vista no hay forma de pedir datos de otro comercio.
 */
@Injectable()
export class PaymentsDashboardRemoteDataSource extends PaymentsDashboardRepository {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.apiUrl}/api/v1/payments/dashboard/`;
  /** Mismo criterio que global-summary: 20 s para no colgar la pantalla. */
  private readonly HTTP_TIMEOUT_MS = 20000;

  override getDashboard(query: PaymentsDashboardQuery): Observable<PaymentsDashboardData> {
    let params = new HttpParams().set('period', query.period);
    if (query.period === 'custom' && query.dateFrom && query.dateTo) {
      params = params.set('date_from', query.dateFrom).set('date_to', query.dateTo);
    }
    return this.http.get<PaymentsDashboardDto>(this.url, { params }).pipe(
      timeout(this.HTTP_TIMEOUT_MS),
      map(mapPaymentsDashboard),
      catchError((error: unknown) => throwError(() => toPaymentsDashboardError(error))),
    );
  }
}
